import { Colors } from "@zapier/design-system";
import { Tooltip } from "@zapier/design-system-beta";
import { Icon } from "@zapier/zinnia-icons";
import { useCallback } from "react";
import { useAtom } from "jotai";
import {
  cursorAtom,
  selectedNodeTypeAtom,
  CursorStates,
} from "@zapier/canvas-state";

import { PathConnectorIcon } from "../icons/PathConnectorIcon";
import { PointerIcon } from "../icons/PointerIcon";

import styles from "./DiagrammingToolbar.module.css";

type Props = { isDisabled: boolean };

const SELECT_TOOL = "Select tool";
const HAND_TOOL = "Hand tool";
const CONNECTOR_TOOL = "Connector tool";

export function CursorOptions({ isDisabled }: Props) {
  const [cursorMode, setCursorMode] = useAtom(cursorAtom);
  const [selectedNodeType, setSelectedNodeType] = useAtom(selectedNodeTypeAtom);

  const toolSelectionListItemClass = isDisabled
    ? styles.toolSelectionButtonWrapperDisabled
    : styles.toolSelectionButtonWrapper;

  const resetCursorMode = useCallback(() => {
    setCursorMode("select");
  }, [setCursorMode]);

  const clearSelectedNodeType = useCallback(() => {
    if (selectedNodeType !== null) {
      setSelectedNodeType(null); // Or else we will end up creating extra nodes at the same time
    }
  }, [selectedNodeType, setSelectedNodeType]);

  const maybeSetCursorMode = useCallback(
    (selectedCursorMode: CursorStates) => {
      if (selectedCursorMode === cursorMode) {
        resetCursorMode();
      } else {
        if (selectedCursorMode !== "select") {
          clearSelectedNodeType();
        }
        setCursorMode(selectedCursorMode);
      }
    },
    [cursorMode, setCursorMode, resetCursorMode, clearSelectedNodeType],
  );

  return (
    <ul className={styles.cursorList}>
      <li className={toolSelectionListItemClass}>
        <Tooltip placement="top" content={isDisabled ? "" : SELECT_TOOL}>
          <Tooltip.FocusableTrigger>
            <button
              aria-label={SELECT_TOOL}
              disabled={isDisabled}
              onClick={() => maybeSetCursorMode("select")}
              onKeyDown={() => maybeSetCursorMode("select")}
              className={styles.toolSelectionButton}
            >
              <PointerIcon isSelected={cursorMode === "select"} />
            </button>
          </Tooltip.FocusableTrigger>
        </Tooltip>
      </li>
      <li className={toolSelectionListItemClass}>
        <Tooltip placement="top" content={isDisabled ? "" : HAND_TOOL}>
          <Tooltip.FocusableTrigger>
            <button
              aria-label={HAND_TOOL}
              disabled={isDisabled}
              onClick={() => maybeSetCursorMode("pan")}
              onKeyDown={() => maybeSetCursorMode("pan")}
              className={styles.toolSelectionButton}
            >
              <Icon
                name={cursorMode === "pan" ? "miscHandFill" : "miscHand"}
                size={24}
                color={
                  cursorMode === "pan" ? "UiPrimaryStrongest" : "currentColor"
                }
              />
            </button>
          </Tooltip.FocusableTrigger>
        </Tooltip>
      </li>
      <li className={toolSelectionListItemClass}>
        <Tooltip placement="top" content={isDisabled ? "" : CONNECTOR_TOOL}>
          <Tooltip.FocusableTrigger>
            <button
              aria-label={CONNECTOR_TOOL}
              disabled={isDisabled}
              onClick={() => maybeSetCursorMode("connect")}
              onKeyDown={() => maybeSetCursorMode("connect")}
              className={styles.toolSelectionButton}
            >
              <PathConnectorIcon
                color={
                  cursorMode === "connect"
                    ? Colors.UiPrimaryStrongest
                    : "currentColor"
                }
              />
            </button>
          </Tooltip.FocusableTrigger>
        </Tooltip>
      </li>
    </ul>
  );
}
